/**
 * AB#1984 — Derive how trustworthy the `business.me` load is.
 *
 * Kept in its own dependency-free module (no react-hook-form / zod imports)
 * so it can be unit-tested without dragging the whole Business Details
 * component — and its ESM-only zod resolver — through the Jest transform.
 *
 * The JWT `businessId` is authoritative: if it is present, the owner HAS a
 * business. `getMyBusiness()` swallows a 404 into `null` by design (the
 * create-flow relies on that), so a transient BE hiccup can surface as a
 * null/empty body WITHOUT throwing — `business == null` alone is therefore
 * NOT proof the owner has no business. The JWT breaks the tie:
 *
 *   • businessLoadFailed — the JWT says a business exists but the query came
 *     back empty (transient 404/null). Treat as an error/retry state, never
 *     as "no business yet".
 *   • noBusinessYet — a genuinely-new owner: the query is empty AND the JWT
 *     agrees (no businessId).
 *   • canHydrate — the response is trustworthy enough to `reset()` the form
 *     from. We must NEVER reset from a loading / errored / anomalous load;
 *     doing so wiped the owner's saved nameEn in the 2026-07-07 incident.
 */
export interface BusinessLoadState {
    businessLoadFailed: boolean;
    noBusinessYet: boolean;
    canHydrate: boolean;
}

export function deriveBusinessLoadState(params: {
    isLoading: boolean;
    isError: boolean;
    business: unknown;
    businessId: number | null | undefined;
}): BusinessLoadState {
    const { isLoading, isError, business, businessId } = params;
    const settled = !isLoading && !isError;
    const businessLoadFailed = settled && business == null && businessId != null;
    const noBusinessYet = settled && business == null && businessId == null;
    const canHydrate = settled && !businessLoadFailed;
    return { businessLoadFailed, noBusinessYet, canHydrate };
}
